import React, { useEffect, useState } from 'react';
import { Modal, Button } from "react-bootstrap";
import { useWeeklyPulse } from '../hooks/useWeeklyPulse';
import { useSelector } from 'react-redux';
import EmojiSelector from './EmojiSelector';
import FeedbackChips from './FeedbackChips';
import EditorComponent from './Editor/EditorComponent';
import { useLocalImageUpload } from './Editor/useLocalImageUpload';
import { convertToRaw, EditorState } from 'draft-js';
import BackgroundAlert from './BackgroundAlert';
import { addDays, startOfDay, subDays } from 'date-fns';

export function WeeklyPulseModal() {
  const [modalVisibility, setModalVisibility] = useState(false);
  const [type, setType] = useState<"success" | "danger">("danger");
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitIsBlocked, setSubmitIsBlocked] = useState(true);

  const { user } = useSelector((state: any) => ({
    user: state.Auth.user,
  }));
  
  const { status, closeModal, openModal, answerPulse, isLoading, isSubmitting } = useWeeklyPulse();

  const [editorFeedback, setEditorFeedback] = useState(
    EditorState.createEmpty()
  );

  const [feelingEmojiSelectedId, setFeelingEmojiSelectedId] = useState<number | null>(null);

  const [selectedPositivos, setSelectedPositivos] = useState<number[]>([]);
  const [selectedNegativos, setSelectedNegativos] = useState<number[]>([]);

  const [responsePeriod, setResponsePeriod] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  const { onLocalFileSelected, uploadPendingImages } = useLocalImageUpload();

  const colaborador_id = user?.usuario?.colaborador?.id

  useEffect(() => {
    const isEmojiSelected = feelingEmojiSelectedId !== null && feelingEmojiSelectedId !== undefined;
    
    setSubmitIsBlocked(!isEmojiSelected || isSubmitting);

    const hoje = new Date();
    const diaDaSemana = hoje.getDay();

    let sextaInicioCicloAtual;

    if (diaDaSemana === 5) {
      sextaInicioCicloAtual = startOfDay(hoje);
    } else {
      const diasDesdeSexta = (diaDaSemana + 1) % 7 + 1;
      sextaInicioCicloAtual = startOfDay(subDays(hoje, diasDesdeSexta));
    }

    const sextaInicioCicloAnterior = subDays(sextaInicioCicloAtual, 7);
    const quintaFechaCicloAnterior = addDays(sextaInicioCicloAnterior, 6);

    setResponsePeriod(
      `Período: ${sextaInicioCicloAnterior.toLocaleDateString()} - ${quintaFechaCicloAnterior.toLocaleDateString()}`
    );

  }, [feelingEmojiSelectedId, isSubmitting]);

  if (isLoading || !colaborador_id) return null;

  const onEditorFeedbackStateChange = (editorState: EditorState) => {
    setEditorFeedback(editorState);
  };

  const handleAnswerPulse = async () => {
    const rawContent = convertToRaw(editorFeedback.getCurrentContent());

    setUploadingImages(true);
    try {
      await uploadPendingImages(rawContent);
    } catch (error) {
      setUploadingImages(false);
      setType("danger");
      setModalVisibility(true);
      setApiError("Não foi possível enviar uma ou mais imagens. Tente novamente.");
      return;
    }
    setUploadingImages(false);

    const feedbackInputValue = JSON.stringify(rawContent);

    try {
      await answerPulse({
        emoji_id: feelingEmojiSelectedId,
        feedback_conteudo: feedbackInputValue,
        tags_positivas: selectedPositivos,
        tags_negativas: selectedNegativos,
        colaborador_id
      })

      setType("success");

      setSuccessMessage('Pulse de sentimento da semana preenchido com sucesso');

      setModalVisibility(true);
    } catch (error) {
      setType("danger");
      
      setModalVisibility(true);
      // @ts-expect-error tipagem 
      setApiError(error?.response?.data?.error);
    }
  }

  return (
    <div style={{
      width: '100vw',
      height: status === 'open' ? '10vh' : 0,
      zIndex: 1002,
      position: 'absolute'
    }}>
      {modalVisibility ? (
        <BackgroundAlert
          type={type}
          closeModal={setModalVisibility}
          modalText={type === "success" ? successMessage : apiError}
        />
      ) : null}
      <Modal show={status === 'open'} onHide={closeModal} fullWidth dialogClassName="modal-dialog-centered" >
        <Modal.Header style={{
          paddingLeft: 32,
          paddingRight: 32,
          borderBottom: '2px solid #d8d8d8',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }} onHide={closeModal} closeButton >
          <div>
            <Modal.Title style={{
              margin: 0,
              fontSize: 20
              }}>Como foi o trabalho essa semana?</Modal.Title>
            <span style={{
              fontSize: 14,
              color: '#6c757d',
              marginTop: 4,
              userSelect: 'none'
            }}>
              {responsePeriod}
            </span>
          </div>
        </Modal.Header>
        <Modal.Body style={{
          paddingLeft: 32,
          paddingRight: 0,
          paddingTop: 0,
          userSelect: 'none'
        }}>
          <div style={{
            maxHeight: '80vh',
            overflowY: 'auto',
            paddingTop: 16,
            paddingRight: 32,
          }}>
            <p style={{
              textAlign: 'center',
              color: '#3d3d3d',
              marginBottom: 24
            }}>
              Reflita sobre suas atividades, projetos, interações, dinâmicas e desempenho, e selecione o emoji que melhor representa como você está se sentindo.
            </p>

            <EmojiSelector setFeelingEmojiSelectedId={setFeelingEmojiSelectedId} feelingEmojiSelectedId={feelingEmojiSelectedId}/>
            <div style={{
              marginTop: 16,
            }}>
              <FeedbackChips 
                selectedNegativos={selectedNegativos}
                selectedPositivos={selectedPositivos}
                setSelectedNegativos={setSelectedNegativos}
                setSelectedPositivos={setSelectedPositivos}
              />
            </div>

            <EditorComponent
              onEditorStateChange={onEditorFeedbackStateChange}
              editorState={editorFeedback}
              submit={handleAnswerPulse}
              sendButton={false}
              options={["inline", "fontSize", "list", "textAlign", "link"]}
              localImageUpload
              onLocalFileSelected={onLocalFileSelected}
            />
          </div>

          <div style={{
            marginTop: 16,
            paddingRight: 32,
            display: 'flex',
            justifyContent: 'end',
            gap: 16
          }}>
            <Button 
              variant="light" onClick={closeModal}>Cancelar</Button>
            <Button
              onClick={() => {
                handleAnswerPulse()
              }}
              disabled={submitIsBlocked || uploadingImages}
            >
              {uploadingImages ? "Enviando..." : "Enviar"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {status === 'button' && (
        <Button
          variant="primary"
          className="floating-button"
          onClick={() => openModal()}
        >
            <i className="mdi mdi-emoticon"  style={{
              fontSize: 32
            }}/>
        </Button>
      )}
    </div>
  );
}
